import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Wrap, WrapItem, useToast,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../contexts/auth";
import { getSaudeAnunciosConfig, getSaudeAnunciosPainel } from "../services/api";
import ConfirmDialog from "../components/ConfirmDialog";
import PainelTab from "../components/SaudeAnunciosML/PainelTab";
import ItensTab from "../components/SaudeAnunciosML/ItensTab";
import HistoricoTab from "../components/SaudeAnunciosML/HistoricoTab";
import ConfigTab from "../components/SaudeAnunciosML/ConfigTab";
import {
  FILTROS_ITENS_PADRAO, VERDE, contasSomentePausadas, iaPausada, montarOpcoes, useMontado,
} from "../components/SaudeAnunciosML/comum";

const CHAVE_ABA = "saude_anuncios_ml_aba";
const ABAS = ["painel", "anuncios", "historico", "configuracoes"];
// No celular o rótulo curto deixa as quatro abas caberem sem rolar.
const TITULOS_ABAS = [
  { curto: "Painel", longo: "Painel" },
  { curto: "Anúncios", longo: "Anúncios" },
  { curto: "Histórico", longo: "Histórico" },
  { curto: "Config.", longo: "Configurações" },
];
const ABA_PAINEL = 0;
const ABA_ANUNCIOS = 1;
const ABA_CONFIG = 3;
const INTERVALO_PAINEL = 30000;

/** "?aba=historico" (ou o número da aba) abre direto nela. */
const indiceDaAba = (valor) => {
  if (valor === null || valor === undefined || valor === "") return null;
  const porNome = ABAS.indexOf(String(valor).toLowerCase());
  if (porNome >= 0) return porNome;
  const n = Number(valor);
  return Number.isInteger(n) && n >= 0 && n < ABAS.length ? n : null;
};

const lerAba = () => {
  try {
    return indiceDaAba(window.localStorage.getItem(CHAVE_ABA)) ?? ABA_PAINEL;
  } catch (e) {
    return ABA_PAINEL;
  }
};

const gravarAba = (i) => {
  try {
    window.localStorage.setItem(CHAVE_ABA, String(i));
  } catch (e) {
    // sem armazenamento local: só não lembra a aba
  }
};

/** A lista rola para a direita? (no celular estreito a última aba pode ficar escondida) */
function useRolaMais(ref) {
  const [mais, setMais] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const medir = () => setMais(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    medir();
    el.addEventListener("scroll", medir, { passive: true });
    window.addEventListener("resize", medir);
    const observador = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    if (observador) observador.observe(el);
    return () => {
      el.removeEventListener("scroll", medir);
      window.removeEventListener("resize", medir);
      if (observador) observador.disconnect();
    };
  }, [ref]);
  return mais;
}

/**
 * Saúde dos Anúncios ML: todo produto com estoque e sem bloqueio tem de estar
 * ativo no Mercado Livre, em cada conta, sem duplicidade. Mostra o diagnóstico
 * da varredura diária, os anúncios com problema, o histórico do que foi feito
 * (ou seria, em simulação) e as configurações do ciclo automático.
 */
export default function SaudeAnunciosML() {
  const auth = useContext(AuthContext);
  const isAdmin = Number(auth?.user?.tipo) === 1;
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [aba, setAba] = useState(() => indiceDaAba(params.get("aba")) ?? lerAba());
  const [dias, setDias] = useState(30);
  const [diasCarregados, setDiasCarregados] = useState(null);
  const [painel, setPainel] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [filtrosItens, setFiltrosItens] = useState(FILTROS_ITENS_PADRAO);
  const [configSuja, setConfigSuja] = useState(false);
  const [abaPendente, setAbaPendente] = useState(null);
  const [somentePausadas, setSomentePausadas] = useState(null);
  const montado = useMontado();
  const ultima = useRef(0);
  const diasDosDados = useRef(null);
  const listaAbas = useRef(null);
  const abasRolam = useRolaMais(listaAbas);

  // O painel traz também os rótulos, os nomes das contas e a configuração que as outras abas usam.
  const carregarPainel = useCallback(async ({ silencioso = false } = {}) => {
    const minha = ++ultima.current;
    if (!silencioso) setCarregando(true);
    const r = await getSaudeAnunciosPainel({ dias });
    if (minha !== ultima.current) return; // resposta atrasada (ou a tela já fechou)
    setCarregando(false);
    if (r) {
      diasDosDados.current = dias;
      setPainel(r);
      setDiasCarregados(dias);
      setErro(false);
      setAtualizadoEm(new Date());
      return;
    }
    setErro(true);
    // Troca de período que falhou: o botão volta para o período dos números que estão na tela.
    const anterior = diasDosDados.current;
    if (anterior !== null && anterior !== dias) {
      setDias(anterior);
      toast({
        title: `Não deu para carregar ${dias} dias.`,
        description: `Continua mostrando ${anterior} dias.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [dias, toast]);

  useEffect(() => { carregarPainel(); }, [carregarPainel]);
  useEffect(() => () => { ultima.current += 1; }, []);

  // Contas em "só pausados" pela configuração: reserva do selo do Painel enquanto o painel não carregou.
  const carregarSomentePausadas = useCallback(async () => {
    const r = await getSaudeAnunciosConfig();
    if (montado.current && r) setSomentePausadas(contasSomentePausadas(r));
  }, [montado]);
  useEffect(() => { carregarSomentePausadas(); }, [carregarSomentePausadas]);

  // A cada 30 s, só com a aba Painel aberta e a página visível; ao voltar para a página, atualiza na hora.
  const noPainel = aba === ABA_PAINEL;
  useEffect(() => {
    if (!noPainel) return undefined;
    const atualizar = () => { if (!document.hidden) carregarPainel({ silencioso: true }); };
    const id = setInterval(atualizar, INTERVALO_PAINEL);
    document.addEventListener("visibilitychange", atualizar);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", atualizar);
    };
  }, [noPainel, carregarPainel]);

  const irParaAba = useCallback((i) => {
    setAba(i);
    gravarAba(i);
    // Volta ao Painel com dado fresco; nas outras abas, tenta de novo se o painel (rótulos) não veio.
    if (i === ABA_PAINEL || !painel) carregarPainel({ silencioso: true });
  }, [carregarPainel, painel]);

  // Sair da Configuração com alteração não salva pergunta antes (a aba desmonta e perderia o que foi digitado).
  const mudarAba = useCallback((i) => {
    if (i === aba) return;
    if (aba === ABA_CONFIG && configSuja) {
      setAbaPendente(i);
      return;
    }
    irParaAba(i);
  }, [aba, configSuja, irParaAba]);

  const sairSemSalvar = () => {
    const destino = abaPendente;
    setAbaPendente(null);
    setConfigSuja(false);
    if (destino !== null) irParaAba(destino);
  };

  // Deep-link: abre a aba pedida e limpa o "?aba=" (o menu lateral volta a marcar a página).
  useEffect(() => {
    if (!params.has("aba")) return;
    const pedida = indiceDaAba(params.get("aba"));
    if (pedida !== null) {
      if (pedida === aba) gravarAba(pedida);
      else mudarAba(pedida);
    }
    const resto = new URLSearchParams(params);
    resto.delete("aba");
    setParams(resto, { replace: true });
  }, [params, setParams, aba, mudarAba]);

  const verItens = useCallback((filtros) => {
    setFiltrosItens({ ...FILTROS_ITENS_PADRAO, ...filtros });
    mudarAba(ABA_ANUNCIOS);
  }, [mudarAba]);

  const irConfig = useCallback(() => mudarAba(ABA_CONFIG), [mudarAba]);
  const atualizarSilencioso = useCallback(() => carregarPainel({ silencioso: true }), [carregarPainel]);
  // Salvou a Configuração: o PUT devolve as contas em "só pausados"; sem elas, relê.
  const aoSalvarConfig = useCallback((r) => {
    carregarPainel({ silencioso: true });
    const lista = contasSomentePausadas(r);
    if (lista) setSomentePausadas(lista);
    else carregarSomentePausadas();
  }, [carregarPainel, carregarSomentePausadas]);

  const opcoes = useMemo(() => montarOpcoes(painel), [painel]);
  const rotulos = painel?.rotulos || null;
  const config = painel?.config || null;

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} mb={3} gap={2} direction={{ base: "column", md: "row" }}>
        <Box minW={0} flex="1">
          <Text as="h1" fontSize="2xl" fontWeight="bold" color={VERDE} lineHeight="short">Saúde dos Anúncios ML</Text>
          <Text fontSize="sm" color="gray.600">
            Todo produto com estoque e sem bloqueio ativo no Mercado Livre, em cada conta e sem duplicidade.
          </Text>
        </Box>
        {config && (
          <Wrap spacing={2} flexShrink={0} aria-label="Estado do sistema">
            <WrapItem>
              <Badge colorScheme={config.ativo ? "green" : "gray"} fontSize="0.75em" px={2} py={0.5}>
                {config.ativo ? "Ligado" : "Desligado"}
              </Badge>
            </WrapItem>
            {config.simulacao && (
              <WrapItem><Badge colorScheme="purple" fontSize="0.75em" px={2} py={0.5}>Simulação</Badge></WrapItem>
            )}
            {config.ia_ativa === false && (
              <WrapItem><Badge colorScheme="gray" fontSize="0.75em" px={2} py={0.5}>IA desligada</Badge></WrapItem>
            )}
            {iaPausada(config) && (
              <WrapItem><Badge colorScheme="red" fontSize="0.75em" px={2} py={0.5}>IA pausada</Badge></WrapItem>
            )}
          </Wrap>
        )}
      </Flex>

      {/* Sem o painel, os nomes (rótulos) das outras abas aparecem como código: avisa em vez de esconder. */}
      {aba !== ABA_PAINEL && !painel && erro && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm" flexWrap="wrap" gap={2}>
          <AlertIcon />
          <Text flex="1" minW="200px">Rótulos indisponíveis: alguns nomes aparecem como código.</Text>
          <Button size={{ base: "md", lg: "sm" }} variant="outline" bg="white" onClick={() => carregarPainel()} isLoading={carregando}>
            Tentar de novo
          </Button>
        </Alert>
      )}

      <Box bg="white" borderRadius="lg" boxShadow="sm">
        <Tabs colorScheme="orange" index={aba} onChange={mudarAba} isLazy>
          <Box position="relative">
            <TabList ref={listaAbas} px={{ base: 1, md: 4 }} overflowX="auto" overflowY="hidden" whiteSpace="nowrap"
              sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              {TITULOS_ABAS.map(({ curto, longo }) => (
                <Tab key={longo} aria-label={longo} fontWeight="bold" fontSize="sm" flexShrink={0} minH="44px" px={{ base: 3, md: 4 }}>
                  {curto === longo ? longo : (
                    <>
                      <Box as="span" display={{ base: "inline", md: "none" }}>{curto}</Box>
                      <Box as="span" display={{ base: "none", md: "inline" }}>{longo}</Box>
                    </>
                  )}
                </Tab>
              ))}
            </TabList>
            {/* Sem barra de rolagem, um degradê na borda mostra que há mais abas para o lado. */}
            {abasRolam && (
              <Box position="absolute" top={0} right={0} bottom="2px" w="40px" pointerEvents="none" borderTopRightRadius="lg"
                bgGradient="linear(to-r, rgba(255,255,255,0), white 85%)" data-testid="abas-rolam" />
            )}
          </Box>
          <TabPanels>
            <TabPanel p={0}>
              <PainelTab
                painel={painel}
                carregando={carregando}
                erro={erro}
                atualizadoEm={atualizadoEm}
                dias={dias}
                diasDados={diasCarregados}
                onDias={setDias}
                onAtualizar={carregarPainel}
                isAdmin={isAdmin}
                rotulos={rotulos}
                contas={opcoes.contas}
                nomesContas={opcoes.nomesContas}
                somentePausadas={somentePausadas}
                onVerItens={verItens}
                onIrConfig={irConfig}
              />
            </TabPanel>
            <TabPanel p={0}>
              <ItensTab
                rotulos={rotulos}
                opcoes={opcoes}
                config={config}
                isAdmin={isAdmin}
                filtros={filtrosItens}
                onFiltros={setFiltrosItens}
                onMudou={atualizarSilencioso}
              />
            </TabPanel>
            <TabPanel p={0}>
              <HistoricoTab rotulos={rotulos} opcoes={opcoes} config={config} isAdmin={isAdmin} onMudou={atualizarSilencioso} />
            </TabPanel>
            <TabPanel p={0}>
              <ConfigTab isAdmin={isAdmin} nomesContas={opcoes.nomesContas} onSalvo={aoSalvarConfig} onSujo={setConfigSuja} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <ConfirmDialog
        isOpen={abaPendente !== null}
        onClose={() => setAbaPendente(null)}
        onConfirm={sairSemSalvar}
        title="Sair sem salvar?"
        body="Há alterações na Configuração que ainda não foram salvas. Se sair agora, elas se perdem."
        confirmText="Sair sem salvar"
        cancelText="Continuar editando"
        confirmColor="red"
        buttonSize={{ base: "md", lg: "sm" }}
        wrapButtons
      />
    </Box>
  );
}
